/**
 * Converts a hex color string to HSL values (without the hsl() wrapper)
 * suitable for the `--primary` CSS custom property consumed by the Tailwind
 * theme (`hsl(var(--primary))`).
 *
 * @example hexToHslValues("#1e40af") => "217 90% 30%"
 */
export function hexToHslValues(hex: string): string {
  const cleaned = hex.replace("#", "");
  const r = parseInt(cleaned.substring(0, 2), 16) / 255;
  const g = parseInt(cleaned.substring(2, 4), 16) / 255;
  const b = parseInt(cleaned.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  if (max === min) {
    return `0 0% ${Math.round(l * 100)}%`;
  }

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Returns a CSS style object that overrides the `--primary` CSS variable with
 * the given hex color. Returns undefined if no color is provided.
 */
export function primaryColorStyle(
  hexColor: string | undefined | null,
): React.CSSProperties | undefined {
  if (!hexColor) return undefined;
  return { "--primary": hexToHslValues(hexColor) } as React.CSSProperties;
}
